import { useMemo, useState } from "react";
import { asEntry, exerciseUsage, hasVariants, removeExercise, unhideExercise, VARIANTS, type Exercise, type Measure } from "../core/exercises";
import {
  compare, entryComparisons, exerciseEntries, exerciseRecords, METRICS, metricsFor, RECORD_UNITS, series, timedDurations,
  type Comparison, type MetricKey, type NewRecord,
} from "../core/progress";
import { sameVariant, type Session } from "../core/sessions";
import type { Backend } from "../core/storage";
import { setLabels } from "../core/templates";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { LineChart } from "./LineChart";
import { formatBadge, formatDate, formatDay, formatDelta, formatDuration, formatSet, formatValue, recordName, t } from "./texts";

type PageTab = "info" | "history" | "charts" | "records";

/** Exercise page with Strong's tabs: Info, Verlauf (every session with its sets), Diagramme (trends), Rekorde. */
export function ExercisePage(props: {
  db: Backend;
  exercise: Exercise;
  sessions: Session[];
  /** the new records of every session (`recordTimeline`) */
  timeline: Map<string, NewRecord[]>;
  onBack(): void;
  onEdit(): void;
  onChanged(): Promise<void>;
  onRemoved(): Promise<void>;
}) {
  const { db, exercise: e } = props;
  const [tab, setTab] = useState<PageTab>("info");
  const [confirm, setConfirm] = useState<{ text: string; action: string }>();
  const [error, setError] = useState<string>();
  // the variant shown in Verlauf, Diagramme and Rekorde (todo 16: reps and timed are never compared)
  const [variant, setVariant] = useState<Measure>(e.measure);
  const other = VARIANTS.find((m) => m !== e.measure);
  const switchable = hasVariants(e) && !!other && props.sessions.some((s) => s.exercises.some(sameVariant(e, other)));
  const shown = switchable ? variant : e.measure;
  const ev = asEntry(e, { measure: shown })!;
  const entries = useMemo(() => exerciseEntries(props.sessions, sameVariant(e, shown)), [props.sessions, e, shown]);
  useBack(props.onBack);

  const askRemove = async () => {
    const use = await exerciseUsage(db, e.id);
    setConfirm(use.templates || use.sessions
      ? { text: t.exercise.confirmHide(e.name, use.templates, use.sessions), action: t.exercise.hide }
      : { text: t.exercise.confirmDelete(e.name), action: t.exercise.delete });
  };
  const remove = async () => {
    setConfirm(undefined);
    const result = await removeExercise(db, e.id);
    if (result === "hidden") await props.onChanged();
    else await props.onRemoved();
  };
  const unhide = async () => {
    const r = await unhideExercise(db, e.id);
    if (!r.ok) setError(r.errors.map(t.exerciseError).join(" "));
    await props.onChanged();
  };

  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={props.onBack}>{t.back}</button>
        <button className="ghost" onClick={props.onEdit}>{t.exercise.edit}</button>
      </header>
      <h1>{e.name}</h1>
      <div className="segmented">
        {(["info", "history", "charts", "records"] as const).map((k) => (
          <button key={k} className={k === tab ? "active" : ""} onClick={() => setTab(k)}>
            {{ info: t.exercise.tabInfo, history: t.exercise.tabHistory, charts: t.exercise.tabCharts, records: t.exercise.tabRecords }[k]}
          </button>
        ))}
      </div>

      {tab !== "info" && switchable && (
        <div className="segmented">
          {VARIANTS.map((m) => (
            <button key={m} className={m === shown ? "active" : ""} aria-pressed={m === shown} onClick={() => setVariant(m)}>{t.measure(m)}</button>
          ))}
        </div>
      )}
      {tab === "history" && <HistoryPanel key={shown} exercise={ev} entries={entries} timeline={props.timeline} />}
      {tab === "charts" && <ChartsPanel key={shown} exercise={ev} entries={entries} />}
      {tab === "records" && <RecordsPanel key={shown} exercise={ev} entries={entries} />}
      {tab === "info" && (
        <>
          {e.hidden && <p className="notice">{t.exercise.hiddenInfo}</p>}
          <dl className="facts">
            <dt>{t.exercise.equipment}</dt><dd>{t.equipment(e.equipment)}</dd>
            <dt>{t.exercise.bodyPart}</dt><dd>{t.bodyPart(e.bodyPart)}</dd>
            <dt>{t.exercise.measure}</dt><dd>{t.measure(e.measure)}<span className="sub">{t.measureHint(e.measure)}</span></dd>
            <dt>{t.exercise.rest}</dt><dd>{formatDuration(e.restSec)}</dd>
            {e.notes && <><dt>{t.exercise.notes}</dt><dd className="notes">{e.notes}</dd></>}
          </dl>
          {error && <p className="error">{error}</p>}
          {e.hidden
            ? <button className="secondary wide" onClick={unhide}>{t.exercise.unhide}</button>
            : <button className="danger wide" onClick={askRemove}>{t.exercise.delete}</button>}
        </>
      )}

      {confirm && <ConfirmDialog text={confirm.text} confirm={confirm.action} danger onConfirm={remove} onCancel={() => setConfirm(undefined)} />}
    </>
  );
}

type Entries = ReturnType<typeof exerciseEntries>;

/** ▲ better, ▼ worse, ▬ the same as last time; the change against last time and against the best before. */
export function ComparisonLine(props: { c: Comparison; metric: MetricKey; showValue?: boolean }) {
  const { c, metric } = props;
  const unit = METRICS[metric].unit;
  const parts: string[] = [];
  if (c.sinceLast === undefined) parts.push(t.progress.first);
  else parts.push(c.sinceLast === 0 ? t.progress.sameAsLast : t.progress.sinceLast(formatDelta(unit, c.sinceLast)));
  if (c.newBest) parts.push(t.progress.newBest);
  else if (c.sinceBest === 0) parts.push(t.progress.sameAsBest);
  else if (c.sinceBest !== undefined) parts.push(t.progress.sinceBest(formatDelta(unit, c.sinceBest)));
  const arrow = c.trend === "up" ? "▲" : c.trend === "down" ? "▼" : c.trend === "same" ? "▬" : "";
  return (
    <span className={"compare " + (c.trend ?? "")}>
      {arrow && <span className="arrow" aria-hidden>{arrow}</span>}
      {props.showValue && <b>{formatValue(unit, c.value)}</b>}
      <span>{parts.join(" · ")}</span>
    </span>
  );
}

export function Badges(props: { records: NewRecord[] }) {
  if (!props.records.length) return null;
  return <div className="badges">{props.records.map((r, i) => <span key={i} className="pr">🏆 {formatBadge(r)}</span>)}</div>;
}

function HistoryPanel(props: { exercise: Exercise; entries: Entries; timeline: Map<string, NewRecord[]> }) {
  const { exercise: e, entries } = props;
  const comparisons = useMemo(() => entryComparisons(entries, e), [entries, e]);
  if (!entries.length) return <p className="muted-text">{t.progress.none}</p>;
  const key = metricsFor(e)[0];
  return (
    <div className="cards">
      {[...entries].reverse().map((en) => {
        const c = comparisons.get(en.sessionId);
        const labels = setLabels(en.sets);
        return (
          <section key={en.sessionId} className="card static">
            <span className="name">{en.name}</span>
            <span className="sub">{formatDay(en.endedAt)}</span>
            {c && (
              <span className="metric-line">
                {t.progress.metric[key]}{c.durationSec !== undefined ? ` (${formatDuration(c.durationSec)})` : ""}:{" "}
                <ComparisonLine c={c} metric={key} showValue />
              </span>
            )}
            <ol className="set-lines">
              {en.sets.map((s, i) => (
                <li key={i}><span className={"set-tag " + s.type}>{labels[i]}</span>{formatSet(s, e)}</li>
              ))}
            </ol>
            <Badges records={(props.timeline.get(en.sessionId) ?? []).filter((r) => r.exerciseId === e.id && r.measure === e.measure)} />
          </section>
        );
      })}
    </div>
  );
}

function ChartsPanel(props: { exercise: Exercise; entries: Entries }) {
  const { exercise: e, entries } = props;
  const metrics = metricsFor(e);
  const durations = useMemo(() => timedDurations(entries), [entries]);
  const [metric, setMetric] = useState<MetricKey>(metrics[0]);
  const [duration, setDuration] = useState<number | undefined>(durations[0]);
  const key = metrics.includes(metric) ? metric : metrics[0];
  const byDuration = METRICS[key].byDuration;
  const d = byDuration ? (duration !== undefined && durations.includes(duration) ? duration : durations[0]) : undefined;
  const points = useMemo(() => series(entries, key, d), [entries, key, d]);
  const comparisons = useMemo(() => compare(points, key), [points, key]);
  const unit = METRICS[key].unit;
  const format = (v: number) => formatValue(unit, v);

  return (
    <>
      {metrics.length > 1 && (
        <div className="chips">
          {metrics.map((m) => (
            <button key={m} className={"chip" + (m === key ? " on" : "")} aria-pressed={m === key} onClick={() => setMetric(m)}>
              {t.progress.metric[m]}
            </button>
          ))}
        </div>
      )}
      {byDuration && durations.length > 1 && (
        <div className="chips">
          <span className="muted-text">{t.progress.duration}</span>
          {durations.map((x) => (
            <button key={x} className={"chip" + (x === d ? " on" : "")} aria-pressed={x === d} onClick={() => setDuration(x)}>
              {formatDuration(x)}
            </button>
          ))}
        </div>
      )}
      {points.length === 0 ? <p className="muted-text">{entries.length ? t.progress.noPoints : t.progress.none}</p> : (
        <>
          <LineChart key={`${key} ${d}`} points={points} format={format} label={t.progress.chartLabel(t.progress.metric[key])} />
          {points.length === 1 && <p className="muted-text">{t.progress.fewPoints}</p>}
          <ul className="list">
            {[...comparisons].reverse().map((c) => (
              <li key={c.sessionId} className="list-item static">
                <span className="row-between"><span>{formatDate(c.endedAt)}</span><b>{format(c.value)}</b></span>
                <ComparisonLine c={c} metric={key} />
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function RecordsPanel(props: { exercise: Exercise; entries: Entries }) {
  const { exercise: e, entries } = props;
  const records = useMemo(() => exerciseRecords(entries, e), [entries, e]);
  if (!records.length) return <p className="muted-text">{t.progress.noRecords}</p>;
  return (
    <ul className="list">
      {records.map((r) => (
        <li key={`${r.kind} ${r.at}`} className="list-item static">
          <span className="row-between">
            <span>{recordName(r)}</span>
            <b>{formatValue(RECORD_UNITS[r.kind], r.value)}</b>
          </span>
          <span className="sub">{formatDate(r.endedAt)}{r.set && r.kind !== "maxWeight" && r.kind !== "repsAtWeight" ? ` · ${formatSet(r.set, e)}` : ""}</span>
        </li>
      ))}
    </ul>
  );
}

